import type {
  CrmTask,
  Deal,
  DealProductionSummary,
  InvoiceStatus,
  ProductionSupplyReport,
  ProposalStatus,
} from '@veem/schema'
import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import { mul, toMoney } from '../lib/decimal.ts'
import { stageByKey } from './catalog.ts'
import { contactsOf, customerById, type ProtoCustomer } from './customers.ts'
import { personById } from './people.ts'

export type OrderStageStatus = NonNullable<DealProductionSummary['currentStageStatus']>
export type ProductionSupplyItem = ProductionSupplyReport['items'][number]

/**
 * A task as the CRM spec §8.3 defines it. The current API ties every task to a deal; the spec
 * allows a deal, a company, a contact, or (here) a personal task with no link at all.
 */
export type ProtoTask = Omit<CrmTask, 'dealId'> & { dealId: string | null; version: number }

export interface OrderLine {
  id: string
  productId: string
  quantity: string
  /** Price per unit including VAT, in the deal currency. */
  unitPrice: string
}

/** Spec §12.6.2: the append-only work journal of a stage. Nothing here is edited later. */
export interface StageEvent {
  id: string
  at: string
  actorId: string
  kind: 'start' | 'resume' | 'pause' | 'output' | 'complete' | 'photo'
  good?: string
  scrap?: string
  /** Pause reason, or scrap reason for an output entry. */
  reason?: string
  note?: string
}

export const PAUSE_REASONS = [
  'Перерва',
  'Кінець зміни',
  'Немає матеріалу',
  'Поломка обладнання',
  'Чекаю креслення або уточнення',
  'Інше',
] as const

export const SCRAP_REASONS = [
  'Дефект матеріалу',
  'Помилка оператора',
  'Помилка в кресленні',
  'Налаштування обладнання',
  'Інше',
] as const

export interface ProtoStageRun {
  key: string
  label: string
  workCenter: string
  status: OrderStageStatus
  assigneeId: string | null
  equipmentId: string | null
  /** The day the shop manager planned it for (YYYY-MM-DD). */
  scheduledDate: string | null
  good: string
  /** Append-only scrap total for the stage, with reasons in the scrap register. */
  scrap: string
  planned: string
  /** Set while someone is working on it; one running stage per worker and per machine. */
  runningSince: string | null
  pausedReason: string | null
  /** Time worked in closed sessions; the open session adds to it. */
  workedMinutes: number
  events: StageEvent[]
}

export type ProductionPriority = 'low' | 'medium' | 'high'

export const PRIORITY_LABELS: Record<ProductionPriority, string> = {
  low: 'Низький',
  medium: 'Звичайний',
  high: 'Високий',
}

/** `none`: no approved техкарта, so nothing is reserved and the shop starts without an issue. */
export type SupplyState = 'waiting_bom' | 'reserved' | 'shortage' | 'issued' | 'none'

/** One row of a draft технологічна карта: what material, how much per product, at which stage. */
export interface EngineeringBomLine {
  id: string
  materialId: string
  /** Base-unit quantity for one finished product, before waste. */
  perUnit: string
  /** Multiplier: 1.05 means 5% waste. */
  waste: string
  stageKey: string
}

/**
 * A new product's engineering task. The engineer uploads drawings, picks the route and fills the
 * технологічна карта; approval makes the product serial, so its next orders skip engineering.
 */
export type EngineeringSource =
  | { kind: 'order' }
  | { kind: 'manual' }
  | { kind: 'monthly'; rowId: string; month: string; label: string }

export interface StandaloneEngineeringJob {
  id: string
  quantity: string
  request: EngineeringRequest
  files: ProtoFile[]
}

export interface EngineeringRequest {
  instructions: string
  media: ProtoFile[]
  approvedVersion?: number
  baseVersion: number
  priority: ProductionPriority
  source: EngineeringSource
  engineerId: string | null
  /** Other engineers working on the same task besides the responsible one. */
  helperIds?: string[]
  requestedById: string
  state: 'todo' | 'in_progress' | 'done'
  dueAt: string
  note: string
  productId: string
  createdAt: string
  route: string[]
  bom: EngineeringBomLine[]
  approvedAt: string | null
}

export interface ProtoProduction {
  programFiles?: ProtoFile[]
  techCardVersion?: number
  number: string
  createdAt: string
  deadline: string
  priority: ProductionPriority
  supply: SupplyState
  materials: ProductionSupplyItem[]
  stages: ProtoStageRun[]
}

export interface ProtoShipment {
  status: 'ready' | 'shipped' | 'delivered'
  carrier: string
  ttn: string | null
}

export interface ProtoInvoice {
  number: string
  status: InvoiceStatus
  amount: string
  paid: string
  dueAt: string
}

export type ActivityKind =
  | 'status'
  | 'note'
  | 'call'
  | 'email'
  | 'task'
  | 'system'
  | 'production'
  | 'warehouse'
  | 'document'

/** Spec §8.1: append-only. A correction is a new entry, never an edit of an old one. */
export interface ProtoActivity {
  id: string
  at: string
  actorId: string | null
  kind: ActivityKind
  text: string
  /** Staff mentioned in a team note; each gets a notification. */
  mentions?: string[]
  /** A file attached together with the note. */
  file?: string
  /** Before and after values for field changes, shown side by side. */
  change?: { from: string; to: string }
}

export type FileKind = 'contract' | 'drawing' | 'other'

export const FILE_KIND_LABELS: Record<FileKind, string> = {
  contract: 'Договір',
  drawing: 'Креслення',
  other: 'Файл',
}

export interface ProtoFile {
  mimeType?: string
  downloadUrl?: string
  id: string
  name: string
  sizeKb: number
  kind: FileKind
  uploadedById: string
  uploadedAt: string
  /** Stable identity across versions. Files saved before versioning use their own id. */
  lineId?: string
  /** Version number of this file, 1 when missing. */
  revision?: number
  /** What changed in this version, written by the uploader. */
  comment?: string | null
  sizeBytes?: number
  /** Earlier versions, newest first. */
  history?: ProtoFile[]
}

/** Spec §11.1: a versioned КП. Every sent revision stays; a new one supersedes it. */
export interface ProtoProposal {
  id: string
  number: string
  version: number
  status: ProposalStatus
  total: string
  currencyCode: string
  createdAt: string
  authorId: string
  validUntil: string
}

export interface ProtoOrder {
  totals?: { gross: string; vat: string; net: string; uah: string }
  deal: Deal & { version?: number }
  lines: OrderLine[]
  /** A new product needs drawings and a technology card before materials can be reserved. */
  engineering: EngineeringRequest | null
  production: ProtoProduction | null
  shipment: ProtoShipment | null
  invoice: ProtoInvoice | null
  proposalCount?: number
  proposals: ProtoProposal[]
  files: ProtoFile[]
  activity: ProtoActivity[]
}

interface DealSeed {
  id: string
  name: string
  clientId: string
  stage: string
  managerId: string
  sellerEntityId: string | null
  amount: string
  currencyCode?: string
  expectedCloseAt: string | null
  createdAt: string
  internalComment?: string
  contactIds?: string[]
  lostReason?: Deal['lostReason']
  closedAt?: string | null
}

const SELLER_NAMES: Record<string, string> = {
  'se-stryi': 'ТДВ «Стрий Авто»',
  'se-am': 'ТОВ «АМ Автопром»',
  'se-veem': 'ТзОВ «Компанія ВЕЕМ-Металавтопром»',
  'se-brash': 'ТзОВ «БРАШ»',
}

const EUR_RATE = '48.152'

/** Builds a record in the exact shape the API returns, so a screen can swap fixtures for queries. */
export const makeDeal = (
  seed: DealSeed,
  company: ProtoCustomer = customerById(seed.clientId),
  people = contactsOf(company.id),
): Deal => {
  const stage = stageByKey(seed.stage)
  const currencyCode = seed.currencyCode ?? 'UAH'
  const contacts = people
    .filter((contact) => !seed.contactIds || seed.contactIds.includes(contact.id))
    .map((contact) => ({ id: contact.id, fullName: contact.fullName, role: contact.role }))
  return {
    id: seed.id,
    name: seed.name,
    clientId: company.id,
    clientName: company.name,
    clientEmail: company.email ?? people[0]?.email ?? null,
    clientPhone: company.phone ?? people[0]?.phone ?? null,
    stage: stage.key,
    stageCategory: stage.category,
    amount: seed.amount,
    managerId: seed.managerId,
    managerName: personById(seed.managerId).name,
    internalComment: seed.internalComment ?? null,
    nextAction: null,
    nextActionAt: null,
    expectedCloseAt: seed.expectedCloseAt,
    aiScore: null,
    position: '0',
    contacts,
    customFields: {},
    orderId: null,
    orderNumber: null,
    orderArchivedAt: null,
    production: null,
    sellerEntityId: seed.sellerEntityId,
    sellerEntityName: seed.sellerEntityId ? (SELLER_NAMES[seed.sellerEntityId] ?? null) : null,
    currencyCode,
    vatTreatmentId: currencyCode === 'UAH' ? 'vat-20' : 'vat-export',
    vatTreatmentName: currencyCode === 'UAH' ? 'ПДВ 20%' : 'Експорт, 0%',
    vatRate: currencyCode === 'UAH' ? '20.00' : '0.00',
    pricesIncludeVat: true,
    operationType: currencyCode === 'UAH' ? 'domestic' : 'export',
    counterpartyCountryCode: currencyCode === 'UAH' ? 'UA' : 'MD',
    exchangeRateSnapshotId: null,
    requestedRateDate: null,
    effectiveRateDate: null,
    exchangeRate: currencyCode === 'EUR' ? EUR_RATE : null,
    exchangeRateUnits: currencyCode === 'EUR' ? '1' : null,
    exchangeRateProvider: currencyCode === 'EUR' ? 'НБУ' : null,
    uahEquivalent: currencyCode === 'EUR' ? toMoney(mul(seed.amount, EUR_RATE)) : seed.amount,
    lostReason: seed.lostReason ?? null,
    lostNote: null,
    closedAt: seed.closedAt ?? null,
    stageChangedAt: seed.createdAt,
    createdBy: seed.managerId,
    createdAt: seed.createdAt,
    updatedAt: seed.createdAt,
  }
}

export interface OrderSeed {
  deal: DealSeed
  lines: Omit<OrderLine, 'id'>[]
  /** How far the order has travelled; the store derives everything else from this. */
  progress:
    | { kind: 'sales' }
    | {
        kind: 'engineering'
        engineerId: string | null
        dueAt: string
        note: string
        state?: EngineeringRequest['state']
        route?: string[]
        bom?: Omit<EngineeringBomLine, 'id'>[]
      }
    | { kind: 'reserved' }
    | { kind: 'producing'; stageIndex: number; goodInStage: string }
    | { kind: 'ready_to_ship' }
    | { kind: 'shipped'; invoice: Omit<ProtoInvoice, 'amount'> }
    | { kind: 'closed'; invoice: Omit<ProtoInvoice, 'amount'> }
  productionNumber?: string
  productionCreatedAt?: string
  deadline?: string
  priority?: ProductionPriority
}

export const ORDER_SEEDS: OrderSeed[] = createMutable([] as OrderSeed[])

/** Team notes, calls and files already on a few orders, so the timeline is not empty. */
export const SEED_ACTIVITY: Record<string, Omit<ProtoActivity, 'id'>[]> = createMutable(
  {} as Record<string, Omit<ProtoActivity, 'id'>[]>,
)

export const SEED_FILES: Record<string, Omit<ProtoFile, 'id'>[]> = createMutable(
  {} as Record<string, Omit<ProtoFile, 'id'>[]>,
)

export const CRM_TASKS: ProtoTask[] = createMutable([] as ProtoTask[])

/**
 * Spec §11.3: a request to pay a supplier, approved by accounting. It is not an invoice and it
 * never creates stock; only receiving does.
 */
export interface PaymentRequestSeed {
  id: string
  supplier: string
  purpose: string
  amount: string
  dueAt: string
  requestedById: string
  status: 'pending' | 'approved' | 'rejected' | 'paid'
  /** The supplier order it pays for, when it came from purchasing. */
  supplyOrder?: string
  /** Which of our companies pays (spec §12.4: the buying entity). */
  buyerEntityId?: string
  invoice?: string
  createdAt?: string
  comment?: string | null
  fileName?: string | null
  paidAt?: string | null
  paymentReference?: string | null
  /** Spec §11.3: append-only. A correction is a new entry, never an edit. */
  history?: PaymentRequestEvent[]
}

export interface PaymentRequestEvent {
  at: string
  actorId: string
  text: string
  /** Why it was rejected or why a payment mark was undone. */
  reason?: string
}

export const PAYMENT_REQUESTS: PaymentRequestSeed[] = createMutable([] as PaymentRequestSeed[])
